import { inject, Injectable } from '@angular/core';
import { HttpParams, HttpResourceRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { endpoints } from '../../environments/endpoints';
import { ApiService } from '../Modules/Shared/Services/api.service';
import {
    BillingListQueryParams,
    CheckoutSessionResponse,
    ContactResponse,
    EstimateResponse,
    InvoicesResponse,
    OverviewResponse,
    PlansResponse,
    PreviewResponse,
    RetryPaymentResponse,
    SubscriptionMutationResponse,
} from '../Interfaces/api/billing.interface';

@Injectable({
    providedIn: 'root',
})
export class BillingService {
    private _apiService = inject(ApiService);

    overview(skipLoading: boolean = true): HttpResourceRequest {
        return this._apiService.get<OverviewResponse>(endpoints.api.billing.overview, { skipLoading });
    }

    estimate(skipLoading: boolean = true): HttpResourceRequest {
        return this._apiService.get<EstimateResponse>(endpoints.api.billing.estimate, { skipLoading });
    }

    plans(skipLoading: boolean = true): HttpResourceRequest {
        return this._apiService.get<PlansResponse>(endpoints.api.billing.plans, { skipLoading });
    }

    invoices(queryParams?: BillingListQueryParams, skipLoading: boolean = true): HttpResourceRequest {
        const params = this.buildQueryParams(queryParams);
        return this._apiService.get<InvoicesResponse>(endpoints.api.billing.invoices, { params, skipLoading });
    }

    previewPlan(planId: string, skipLoading: boolean = false): Observable<PreviewResponse> {
        const request = this._apiService.post<PreviewResponse>(endpoints.api.billing.previewPlan, {
            body: { plan_id: planId },
            skipLoading,
        });
        return this._apiService.run<PreviewResponse>(request);
    }

    updatePlan(planId: string, skipLoading: boolean = false): Observable<SubscriptionMutationResponse> {
        const request = this._apiService.put<SubscriptionMutationResponse>(endpoints.api.billing.updatePlan, {
            body: { plan_id: planId },
            skipLoading,
        });
        return this._apiService.run<SubscriptionMutationResponse>(request);
    }

    cancel(skipLoading: boolean = false): Observable<SubscriptionMutationResponse> {
        const request = this._apiService.post<SubscriptionMutationResponse>(endpoints.api.billing.cancel, { skipLoading });
        return this._apiService.run<SubscriptionMutationResponse>(request);
    }

    resume(skipLoading: boolean = false): Observable<SubscriptionMutationResponse> {
        const request = this._apiService.post<SubscriptionMutationResponse>(endpoints.api.billing.resume, { skipLoading });
        return this._apiService.run<SubscriptionMutationResponse>(request);
    }

    createPaymentMethodSession(skipLoading: boolean = false): Observable<CheckoutSessionResponse> {
        const request = this._apiService.post<CheckoutSessionResponse>(endpoints.api.billing.paymentMethodSession, {
            skipLoading,
        });
        return this._apiService.run<CheckoutSessionResponse>(request);
    }

    updateContact(email: string, skipLoading: boolean = false): Observable<ContactResponse> {
        const request = this._apiService.put<ContactResponse>(endpoints.api.billing.contact, {
            body: { email },
            skipLoading,
        });
        return this._apiService.run<ContactResponse>(request);
    }

    retryPayment(skipLoading: boolean = false): Observable<RetryPaymentResponse> {
        const request = this._apiService.post<RetryPaymentResponse>(endpoints.api.billing.retryPayment, { skipLoading });
        return this._apiService.run<RetryPaymentResponse>(request);
    }

    private buildQueryParams(queryParams?: BillingListQueryParams): HttpParams {
        let params = new HttpParams();
        params = params.set('pageSize', queryParams?.pageSize?.toString() ?? '10');
        params = queryParams?.page != null ? params.set('page', queryParams.page.toString()) : params;
        return params;
    }
}
